import { useState, useEffect } from 'react';
import axios from 'axios';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';

// 注册 Chart.js 组件
ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const API = 'https://api.nycni-chem-ai-class.top/api/teacher';
const MODULES = ['必修一', '必修二', '选择性必修一', '选择性必修二', '选择性必修三', '全部'];
const DIMENSIONS = ['宏观辨识与微观探析', '变化观念与平衡思想', '证据推理与模型认知', '科学探究与创新意识', '科学态度与社会责任'];

function TeacherPanel() {
  const [classrooms, setClassrooms] = useState([]);
  const [textbooks, setTextbooks] = useState({});
  const [activeClassId, setActiveClassId] = useState(null);
  const [view, setView] = useState('list');
  const [logs, setLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null); // 详情弹窗状态

  // 表单状态
  const [newClassForm, setNewClassForm] = useState({ title: '', textbookVersion: '人教版', module: '必修一', currentChapter: '' });
  const [newTaskForm, setNewTaskForm] = useState({ type: 'teacher_task', question: '', expectedAnswer: '', materials: '', remark: '' });

  useEffect(() => {
    loadData();
    loadTextbooks();
    const interval = setInterval(loadData, 3000);
    return () => clearInterval(interval);
  }, []);

  const loadTextbooks = async () => {
    const res = await axios.get('https://api.nycni-chem-ai-class.top/api/textbooks');
    setTextbooks(res.data);
  };

  const loadData = async () => {
    try {
      const res = await axios.get(`${API}/classrooms`);
      setClassrooms(Array.isArray(res.data) ? res.data : []);
      const logRes = await axios.get(`${API}/logs`);
      setLogs(Array.isArray(logRes.data) ? logRes.data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const createClassroom = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/classrooms`, newClassForm);
    setNewClassForm({ title: '', textbookVersion: '人教版', module: '必修一', currentChapter: '' });
    loadData();
  };

  const deleteClassroom = async (id) => {
    if (window.confirm('确定要删除这个课堂吗？')) {
      await axios.delete(`${API}/classrooms/${id}`);
      loadData();
    }
  };

  const deleteTask = async (classId, taskId) => {
    if (window.confirm('删除此任务？')) {
      await axios.delete(`${API}/classrooms/${classId}/tasks/${taskId}`);
      loadData();
    }
  };

  const activateClassroom = async (id) => {
    await axios.post(`${API}/classrooms/${id}/activate`);
    setActiveClassId(id);
    setView('control');
    loadData();
  };

  const finishClassroom = async (id) => {
    if (window.confirm('确定要结束这堂课吗？')) {
      await axios.post(`${API}/classrooms/${id}/finish`);
      setView('review');
      loadData();
    }
  };

  const addTask = async (e, classId) => {
    e.preventDefault();
    await axios.post(`${API}/classrooms/${classId}/tasks`, newTaskForm);
    setNewTaskForm({ type: 'teacher_task', question: '', expectedAnswer: '', materials: '', remark: '' });
    loadData();
  };

  const deployTask = async (classId, taskId) => {
    await axios.post(`${API}/classrooms/${classId}/tasks/${taskId}/deploy`);
    loadData();
  };

  const clearTask = async (classId) => {
    await axios.post(`${API}/classrooms/${classId}/clear`);
    loadData();
  };

  // 辅助：等级转分数 (用于雷达图)
  const levelToScore = (level) => {
    const map = { 'A': 5, 'B': 4, 'C': 3, 'D': 2, 'E': 1 };
    return map[level] || 0;
  };

  // 辅助：生成雷达图数据
  const generateRadarData = (classLogs) => {
    const sums = [0, 0, 0, 0, 0];
    const counts = [0, 0, 0, 0, 0];

    classLogs.forEach(log => {
      if (log.evaluation_data?.dimensions) {
        log.evaluation_data.dimensions.forEach(dim => {
          const idx = DIMENSIONS.indexOf(dim.name);
          if (idx !== -1) {
            sums[idx] += levelToScore(dim.level);
            counts[idx] += 1;
          }
        });
      }
    });

    const avgScores = sums.map((sum, i) => counts[i] > 0 ? sum / counts[i] : 0);
    
    return {
      labels: DIMENSIONS,
      datasets: [{
        label: '班级综合素养',
        data: avgScores,
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 2,
      }]
    };
  };

  const getLevelColor = (level) => {
    const colors = { 'A': 'bg-green-100 text-green-800', 'B': 'bg-blue-100 text-blue-800', 'C': 'bg-yellow-100 text-yellow-800', 'D': 'bg-orange-100 text-orange-800', 'E': 'bg-red-100 text-red-800' };
    return colors[level] || colors['C'];
  };

  const activeClassroom = Array.isArray(classrooms) ? classrooms.find(c => c.id === activeClassId) : undefined;
  const chapters = textbooks[newClassForm.textbookVersion]?.[newClassForm.module] || [];
  const classLogs = logs.filter(l => l.classroomId === activeClassId);
  const radarData = generateRadarData(classLogs);

  return (
    <div className="max-w-7xl mx-auto p-6 h-[90vh] flex flex-col">
      {/* 顶部导航 */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">老师中控台</h1>
        <div className="space-x-2">
          <button onClick={() => setView('list')} className={`px-4 py-2 rounded-lg ${view === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border'}`}>课堂列表</button>
          {activeClassId && activeClassroom?.status !== 'idle' && (
            <>
              {activeClassroom?.status === 'active' && <button onClick={() => setView('control')} className={`px-4 py-2 rounded-lg ${view === 'control' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border'}`}>实时中控</button>}
              <button onClick={() => setView('review')} className={`px-4 py-2 rounded-lg ${view === 'review' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 border'}`}>课堂回顾</button>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 bg-white rounded-2xl shadow-xl overflow-hidden p-6 relative">
        
        {/* 视图 1: 课堂列表 */}
        {view === 'list' && (
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-bold mb-4">创建新课堂</h3>
              <form onSubmit={createClassroom} className="space-y-4 bg-gray-50 p-6 rounded-xl">
                <input placeholder="课堂名称 (如: 物质的分类与转化)" className="w-full p-3 border rounded-lg" value={newClassForm.title} onChange={e => setNewClassForm({...newClassForm, title: e.target.value})} required />
                <div className="flex gap-4">
                  <select className="flex-1 p-3 border rounded-lg" value={newClassForm.textbookVersion} onChange={e => setNewClassForm({...newClassForm, textbookVersion: e.target.value, currentChapter: ''})}>
                    <option>人教版</option>
                    <option>鲁科版</option>
                  </select>
                  <select className="flex-1 p-3 border rounded-lg" value={newClassForm.module} onChange={e => setNewClassForm({...newClassForm, module: e.target.value, currentChapter: ''})}>
                    {MODULES.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">当前进度章节 (AI默认之前已学)</label>
                  <select className="w-full p-3 border rounded-lg" value={newClassForm.currentChapter} onChange={e => setNewClassForm({...newClassForm, currentChapter: e.target.value})}>
                    <option value="">请选择...</option>
                    {chapters.map(ch => <option key={ch} value={ch}>{ch}</option>)}
                  </select>
                </div>
                <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold">创建课堂项目</button>
              </form>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4">我的课堂</h3>
              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                {classrooms.map(c => (
                  <div key={c.id} className="border p-4 rounded-xl flex justify-between items-center group hover:shadow-md transition">
                    <div>
                      <h4 className="font-bold text-gray-800 flex items-center gap-2">
                        {c.title}
                        {c.status === 'active' && <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded animate-pulse">上课中</span>}
                        {c.status === 'finished' && <span className="text-xs bg-gray-500 text-white px-2 py-0.5 rounded">已归档</span>}
                      </h4>
                      <p className="text-sm text-gray-500">{c.textbookVersion} {c.module}</p>
                    </div>
                    <div className="flex gap-2">
                       {c.status === 'idle' && (
                         <>
                          <button onClick={() => { setActiveClassId(c.id); setView('detail'); }} className="px-3 py-1 border rounded text-sm hover:bg-gray-50">编辑</button>
                          <button onClick={() => activateClassroom(c.id)} className="px-3 py-1 bg-green-600 text-white rounded text-sm">开始上课</button>
                         </>
                       )}
                       {c.status === 'active' && (
                         <>
                          <button onClick={() => { setActiveClassId(c.id); setView('control'); }} className="px-3 py-1 bg-blue-600 text-white rounded text-sm">进入中控</button>
                          <button onClick={() => finishClassroom(c.id)} className="px-3 py-1 bg-red-500 text-white rounded text-sm">结束课堂</button>
                         </>
                       )}
                       {c.status === 'finished' && (
                         <button onClick={() => { setActiveClassId(c.id); setView('review'); }} className="px-3 py-1 bg-purple-600 text-white rounded text-sm">查看报告</button>
                       )}
                       <button onClick={() => deleteClassroom(c.id)} className="px-3 py-1 text-red-500 hover:bg-red-50 rounded text-sm">删除</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 视图 1.5: 编辑任务 */}
        {view === 'detail' && activeClassroom && (
           <div>
             <button onClick={() => setView('list')} className="text-blue-600 mb-4 hover:underline">&larr; 返回列表</button>
             <div className="grid grid-cols-2 gap-8">
               <div>
                 <h3 className="text-xl font-bold mb-4">{activeClassroom.title} - 任务预设</h3>
                 <form onSubmit={(e) => addTask(e, activeClassroom.id)} className="space-y-4 bg-gray-50 p-6 rounded-xl">
                   <div className="flex gap-4">
                     <label className="flex items-center cursor-pointer"><input type="radio" name="type" value="teacher_task" checked={newTaskForm.type === 'teacher_task'} onChange={e => setNewTaskForm({...newTaskForm, type: e.target.value})} className="mr-2" /> 教师布置题</label>
                     <label className="flex items-center cursor-pointer"><input type="radio" name="type" value="student_question" checked={newTaskForm.type === 'student_question'} onChange={e => setNewTaskForm({...newTaskForm, type: e.target.value})} className="mr-2" /> 学生提问引导</label>
                   </div>
                   <input placeholder="问题内容" className="w-full p-3 border rounded-lg" value={newTaskForm.question} onChange={e => setNewTaskForm({...newTaskForm, question: e.target.value})} />
                   <textarea placeholder="期望答案/引导方向" className="w-full p-3 border rounded-lg" rows="2" value={newTaskForm.expectedAnswer} onChange={e => setNewTaskForm({...newTaskForm, expectedAnswer: e.target.value})} />
                   <input placeholder="参考资料/页码" className="w-full p-3 border rounded-lg" value={newTaskForm.materials} onChange={e => setNewTaskForm({...newTaskForm, materials: e.target.value})} />
                   <textarea placeholder="学情备注 (如：学生基础较弱，暂不要求计算)" className="w-full p-3 border rounded-lg" rows="2" value={newTaskForm.remark} onChange={e => setNewTaskForm({...newTaskForm, remark: e.target.value})} />
                   <button className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700">+ 添加到任务库</button>
                 </form>
               </div>
               <div>
                 <h3 className="text-lg font-bold mb-4">当前任务库 ({activeClassroom.tasks?.length || 0})</h3>
                 <div className="space-y-3 max-h-[500px] overflow-y-auto">
                   {activeClassroom.tasks?.map(t => (
                     <div key={t.id} className="bg-white border p-4 rounded-lg shadow-sm flex justify-between items-start">
                       <div>
                         <span className={`text-xs px-2 py-0.5 rounded ${t.type === 'teacher_task' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                           {t.type === 'teacher_task' ? '课堂作答' : '自由提问'}
                         </span>
                         <p className="font-medium mt-2">{t.question}</p>
                         {t.remark && <p className="text-xs text-gray-400 mt-1">备注: {t.remark}</p>}
                       </div>
                       <button onClick={() => deleteTask(activeClassroom.id, t.id)} className="text-red-400 hover:text-red-600">✕</button>
                     </div>
                   ))}
                   {(!activeClassroom.tasks || activeClassroom.tasks.length === 0) && <p className="text-gray-400 text-center py-10">暂无任务</p>}
                 </div>
               </div>
             </div>
           </div>
        )}

        {/* 视图 2: 实时中控 */}
        {view === 'control' && activeClassroom && (
          <div>
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl mb-6 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-blue-800">正在上课: {activeClassroom.title}</h2>
                <p className="text-blue-600 text-sm">{activeClassroom.textbookVersion} {activeClassroom.module} · {activeClassroom.currentChapter}</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">当前状态</div>
                {activeClassroom.activeTaskId ? (
                  <span className="text-red-600 font-bold animate-pulse">🔴 任务进行中</span>
                ) : (
                  <span className="text-green-600 font-bold">🟢 等待中 (可自由提问)</span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-6 h-[500px]">
              <div className="col-span-2 overflow-y-auto pr-2">
                <h3 className="font-bold text-lg mb-4">课堂任务库 (点击布置)</h3>
                <div className="grid grid-cols-2 gap-4">
                  {activeClassroom.tasks?.map(t => (
                    <div key={t.id} className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${activeClassroom.activeTaskId === t.id ? 'border-red-500 bg-red-50 shadow-lg' : 'border-gray-200 hover:border-blue-400 hover:shadow-md'}`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className={`text-xs px-2 py-1 rounded ${t.type === 'teacher_task' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                          {t.type === 'teacher_task' ? '学生作答' : '自由引导'}
                        </span>
                      </div>
                      <p className="font-bold text-lg">{t.question}</p>
                      {t.materials && <p className="text-sm text-gray-500 mt-1">📖 {t.materials}</p>}
                      <div className="mt-5 flex gap-2">
                        {activeClassroom.activeTaskId === t.id ? (
                          <button onClick={() => clearTask(activeClassroom.id)} className="flex-1 py-2 bg-red-500 text-white rounded-lg text-sm font-bold">结束任务</button>
                        ) : (
                          <button onClick={() => deployTask(activeClassroom.id, t.id)} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700">布置给学生</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="border-l pl-6 flex flex-col">
                <h3 className="font-bold text-lg mb-4">实时学生动态</h3>
                <div className="flex-1 space-y-3 overflow-y-auto">
                  {classLogs.slice(0, 20).map(log => (
                    <div key={log.id} className="bg-gray-50 p-3 rounded-lg text-sm cursor-pointer hover:bg-gray-100 border border-transparent hover:border-blue-200" onClick={() => setSelectedLog(log)}>
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-700">{log.type === 'answer' ? '📝 答题' : '❓ 提问'}</span>
                        {log.evaluation_data && (
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${getLevelColor(log.evaluation_data.overallLevel)}`}>
                            {log.evaluation_data.overallLevel}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 truncate mt-1">{log.studentInput}</p>
                      <p className="text-xs text-gray-400 mt-1">{new Date(log.timestamp).toLocaleTimeString()}</p>
                    </div>
                  ))}
                  {classLogs.length === 0 && <p className="text-gray-400 text-center py-10">暂无学生提交</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 视图 3: 课堂回顾 */}
        {view === 'review' && activeClassroom && (
          <div className="h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">{activeClassroom.title} - 课堂报告</h2>
              <button onClick={() => setView('list')} className="text-gray-500 hover:text-gray-700">返回列表</button>
            </div>
            
            <div className="grid grid-cols-3 gap-8 flex-1">
              {/* 左侧：雷达图 */}
              <div className="bg-gray-50 p-6 rounded-xl flex flex-col items-center justify-center">
                <h3 className="text-lg font-bold mb-4">班级核心素养综合雷达图</h3>
                <div className="w-full h-80">
                  <Radar data={radarData} options={{
                    scales: { r: { beginAtZero: true, max: 5, ticks: { stepSize: 1 } } }
                  }} />
                </div>
              </div>
              
              {/* 右侧：详细记录 */}
              <div className="col-span-2 flex flex-col">
                <h3 className="text-lg font-bold mb-4">课堂交互记录 ({classLogs.length})</h3>
                <div className="flex-1 overflow-y-auto space-y-4">
                  {classLogs.map(log => (
                    <div key={log.id} className="border p-4 rounded-xl hover:shadow-md cursor-pointer" onClick={() => setSelectedLog(log)}>
                      <div className="flex justify-between">
                        <div className="flex items-center gap-3">
                          <span className="font-bold">{log.studentId}</span>
                          <span className="text-sm text-gray-500">{log.type === 'answer' ? '回答任务' : '自由提问'}</span>
                        </div>
                        {log.evaluation_data && (
                          <span className={`px-3 py-1 rounded-full text-sm font-bold ${getLevelColor(log.evaluation_data.overallLevel)}`}>
                            {log.evaluation_data.overallLevel}
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-gray-800 font-medium">Q: {log.studentInput}</p>
                      <p className="mt-1 text-gray-600 text-sm line-clamp-2">A: {log.aiResponse}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 详情弹窗 */}
        {selectedLog && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedLog(null)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
                <h3 className="text-xl font-bold">详情查看</h3>
                <button onClick={() => setSelectedLog(null)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-500">学生ID: {selectedLog.studentId}</span>
                    {selectedLog.evaluation_data && (
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${getLevelColor(selectedLog.evaluation_data.overallLevel)}`}>
                        综合等级: {selectedLog.evaluation_data.overallLevel}
                      </span>
                    )}
                  </div>
                  <div className="bg-blue-50 p-4 rounded-xl">
                    <p className="text-sm text-blue-600 font-medium mb-1">学生输入</p>
                    <p className="text-lg text-gray-800">{selectedLog.studentInput}</p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-sm text-gray-600 font-medium mb-1">AI 回复/点评</p>
                  <p className="text-gray-800 whitespace-pre-wrap">{selectedLog.aiResponse}</p>
                </div>

                {selectedLog.evaluation_data?.dimensions && (
                  <div>
                    <p className="text-sm font-bold text-gray-700 mb-3">核心素养评价</p>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedLog.evaluation_data.dimensions.map((dim, i) => (
                        <div key={i} className="border p-3 rounded-lg flex justify-between items-center">
                          <span className="text-sm">{dim.name}</span>
                          <span className={`px-2 py-1 rounded text-xs font-bold ${getLevelColor(dim.level)}`}>{dim.level}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default TeacherPanel;